import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { contractService } from "@/lib/api";
import { SUPPORTED_LANGUAGES } from "@/lib/language";
import { useSelectedProperty } from "@/stores/property";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

/** 동의 방식 코드 → 라벨. api `contract_service.ACCEPTED_VIA_*` 와 짝. */
const ACCEPTED_VIA_LABEL: Record<string, string> = {
  guest_link_button: "안내 링크 · 동의 버튼",
};

export function ContractsPage() {
  const propertyId = useSelectedProperty();
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState("en");
  // 언어별 로컬 드래프트 — 탭을 오가도 편집 내용이 유실되지 않는다
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // 숙소 전환 시 로컬 드래프트 초기화 — 드래프트가 다른 숙소로 새지 않도록
  useEffect(() => setDrafts({}), [propertyId]);

  const settingsKey = ["contracts", propertyId];
  const { data: settings } = useQuery({
    queryKey: settingsKey,
    queryFn: () => contractService.settings(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const { data: acceptances } = useQuery({
    queryKey: ["contract-acceptances", propertyId],
    queryFn: () => contractService.acceptances(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });

  const current = settings?.contracts.find((c) => c.language === language);
  const draft = drafts[language] ?? current?.body ?? "";

  const invalidate = () => queryClient.invalidateQueries({ queryKey: settingsKey });
  const clearDraft = (lang: string) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[lang];
      return next;
    });

  const saveMutation = useMutation({
    mutationFn: (vars: { language: string; body: string }) =>
      contractService.upsert(propertyId ?? "", vars.language, vars.body),
    onSuccess: (_data, vars) => {
      clearDraft(vars.language);
      toast.success("계약서를 저장했습니다.");
      invalidate();
    },
    onError: () => toast.error("계약서 저장에 실패했습니다."),
  });

  const removeMutation = useMutation({
    mutationFn: (vars: { language: string }) =>
      contractService.remove(propertyId ?? "", vars.language),
    onSuccess: (_data, vars) => {
      clearDraft(vars.language);
      toast.success("계약서를 삭제했습니다.");
      invalidate();
    },
    onError: () => toast.error("계약서 삭제에 실패했습니다."),
  });

  const downloadMutation = useMutation({
    mutationFn: (acceptanceId: string) =>
      contractService.downloadAcceptance(propertyId ?? "", acceptanceId),
    onError: () => toast.error("체결본을 내려받지 못했습니다."),
  });

  const requiredMutation = useMutation({
    mutationFn: (required: boolean) => contractService.setRequired(propertyId ?? "", required),
    onSuccess: invalidate,
    onError: () => toast.error("설정 변경에 실패했습니다."),
  });

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">계약서</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            언어별 숙박 계약서를 관리합니다. 게스트는 언어 선택 직후 계약서에 동의합니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="contract-required"
            checked={settings?.required ?? true}
            onCheckedChange={(v) => requiredMutation.mutate(v)}
          />
          <Label htmlFor="contract-required" className="text-sm">
            동의 필수 (끄면 게스트가 건너뛸 수 있음)
          </Label>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <Tabs value={language} onValueChange={setLanguage}>
            <TabsList>
              {SUPPORTED_LANGUAGES.map((l) => {
                const has = settings?.contracts.some((c) => c.language === l.code);
                return (
                  <TabsTrigger key={l.code} value={l.code}>
                    {l.label}
                    {has ? " ●" : ""}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={draft}
            onChange={(e) => setDrafts((d) => ({ ...d, [language]: e.target.value }))}
            placeholder="이 언어의 계약서 본문(markdown)을 입력하세요. 비워두면 이 언어로는 제공되지 않습니다."
            className="min-h-64 font-mono text-sm"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              게스트 화면에는 예약 정보(게스트명·룸타입·호실·체크인/아웃)가 본문 위에 자동
              표시됩니다. 폴백: 선택 언어 → English → 첫 등록 계약서.
            </p>
            <div className="flex gap-2">
              {current ? (
                <Button
                  variant="outline"
                  disabled={removeMutation.isPending}
                  onClick={() => removeMutation.mutate({ language })}
                >
                  삭제
                </Button>
              ) : null}
              <Button
                disabled={saveMutation.isPending || draft.trim().length === 0}
                onClick={() => saveMutation.mutate({ language, body: draft })}
              >
                저장
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">체결 이력</CardTitle>
        </CardHeader>
        <CardContent>
          {acceptances?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>게스트</TableHead>
                  <TableHead>언어</TableHead>
                  <TableHead>룸타입</TableHead>
                  <TableHead>호실</TableHead>
                  <TableHead>체결 시각</TableHead>
                  <TableHead>동의 방식</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {acceptances.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.guest_name ?? "-"}</TableCell>
                    <TableCell>{a.language}</TableCell>
                    <TableCell>{a.room_type_name ?? "-"}</TableCell>
                    <TableCell>{a.room_number ?? "미배정"}</TableCell>
                    <TableCell className="tabular-nums">
                      {new Date(a.accepted_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {ACCEPTED_VIA_LABEL[a.accepted_via ?? ""] ?? a.accepted_via ?? "기록 없음"}
                    </TableCell>
                    <TableCell className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadMutation.mutate(a.id)}
                        disabled={downloadMutation.isPending}
                      >
                        내려받기
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            스냅샷
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80dvh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>체결 스냅샷 — {a.guest_name ?? a.guest_id}</DialogTitle>
                          </DialogHeader>
                          <div className="chat-prose">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.body}</ReactMarkdown>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">아직 체결된 계약이 없습니다.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
